import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import MessagesReportModal from '../MessagesReportModal';

// Styled Components - Professional Clean Style
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        maxHeight: '85vh',
        height: '85vh',
        width: '100%',
        maxWidth: '600px',
        margin: '16px',
        borderRadius: '12px',
        overflow: 'hidden',
        boxSizing: 'border-box',
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    margin: 0,
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: '72px',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: 0,
    backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const TicketListContainer = styled(List)(({ theme }) => ({
    flex: 1,
    overflow: 'auto',
    overflowX: 'hidden',
    padding: 0,
    margin: 0,
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
    },
}));

const TicketListItemStyled = styled(ListItem)(({ theme }) => ({
    padding: '14px 20px',
    cursor: 'pointer',
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxSizing: 'border-box',
    maxWidth: '100%',
    overflow: 'hidden',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f7fa',
        transform: 'translateX(2px)',
    },
}));

const TicketInfo = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
}));

const TicketHeader = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
    gap: '12px',
}));

const ContactName = styled(Typography)(({ theme }) => ({
    fontSize: '0.95rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
}));

const TicketDate = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    flexShrink: 0,
}));

const LastMessage = styled(Typography)(({ theme }) => ({
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '& > span': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
        minWidth: 0,
    },
}));

const StatusBadge = styled(Box)(({ status, theme }) => {
    let backgroundColor = '#10b981'; // Verde - open

    if (status === 'pending') {
        backgroundColor = '#f59e0b'; // Laranja
    } else if (status === 'closed') {
        backgroundColor = '#6b7280'; // Cinza
    }

    return {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: 600,
        color: '#fff',
        backgroundColor: backgroundColor,
        textTransform: 'uppercase',
        flexShrink: 0,
        letterSpacing: '0.3px',
    };
});

const LoadMoreButton = styled(Button)(({ theme }) => ({
    margin: '16px auto',
    display: 'block',
    width: 'calc(100% - 40px)',
    maxWidth: 'calc(100% - 40px)',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    boxSizing: 'border-box',
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 24px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
}));

const ViewButton = styled(IconButton)(({ theme }) => ({
    marginLeft: '8px',
    flexShrink: 0,
    color: theme.palette.primary.main,
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(25, 118, 210, 0.12)'
            : 'rgba(25, 118, 210, 0.08)',
    },
}));

export default function TicketListModal({ open, onClose, user }) {
    const history = useHistory();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [messagesModalOpen, setMessagesModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    useEffect(() => {
        if (open && user?.id) {
            setPageNumber(1);
            setTickets([]);
            fetchTickets(1);
        }
    }, [open, user?.id]);

    const fetchTickets = async (page) => {
        if (!user?.id) return;

        setLoading(true);
        try {
            const { data } = await api.get('/tickets/user-open', {
                params: {
                    userId: user.id,
                    pageNumber: page,
                }
            });

            if (page === 1) {
                setTickets(data.tickets);
            } else {
                setTickets(prev => [...prev, ...data.tickets]);
            }

            setHasMore(data.hasMore);
            setTotalCount(data.count);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = pageNumber + 1;
        setPageNumber(nextPage);
        fetchTickets(nextPage);
    };

    const handleTicketClick = (ticketId) => {
        history.push(`/tickets/${ticketId}`);
        onClose();
    };

    const handleSpyTicket = (e, ticket) => {
        e.stopPropagation();
        setSelectedTicket(ticket);
        setMessagesModalOpen(true);
    };

    const handleCloseMessagesModal = () => {
        setMessagesModalOpen(false);
        setSelectedTicket(null);
    };

    const formatTicketDate = (dateString) => {
        try {
            const date = parseISO(dateString);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            if (date.toDateString() === today.toDateString()) {
                return format(date, 'HH:mm');
            } else if (date.toDateString() === yesterday.toDateString()) {
                return 'Ontem';
            } else {
                return format(date, 'dd/MM');
            }
        } catch (error) {
            return '';
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            open: 'Aberto',
            pending: 'Pendente',
            closed: 'Fechado',
        };
        return labels[status] || status;
    };

    const truncateMessage = (message, maxLength = 60) => {
        if (!message) return 'Sem mensagens';
        if (message.length <= maxLength) return message;
        return message.substring(0, maxLength) + '...';
    };

    return (
        <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <StyledDialogTitle>
                <Box>
                    <Typography variant="h6" component="div">
                        Atendimentos de {user?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {totalCount} atendimento{totalCount !== 1 ? 's' : ''}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </StyledDialogTitle>

            <StyledDialogContent dividers>
                {loading && tickets.length === 0 ? (
                    <EmptyState>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Carregando atendimentos...</Typography>
                    </EmptyState>
                ) : tickets.length === 0 ? (
                    <EmptyState>
                        <Typography variant="h6">Nenhum atendimento encontrado</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Este atendente ainda não possui atendimentos
                        </Typography>
                    </EmptyState>
                ) : (
                    <>
                        <TicketListContainer>
                            {tickets.map((ticket, index) => (
                                <React.Fragment key={ticket.id}>
                                    <TicketListItemStyled onClick={() => handleTicketClick(ticket.uuid)}>
                                        <ListItemAvatar>
                                            <Avatar
                                                src={ticket.contact?.media || ticket.contact?.profilePicUrl}
                                                alt={ticket.contact?.name}
                                            >
                                                {ticket.contact?.name?.charAt(0)?.toUpperCase()}
                                            </Avatar>
                                        </ListItemAvatar>

                                        <TicketInfo>
                                            <TicketHeader>
                                                <ContactName>
                                                    {ticket.contact?.name || ticket.contact?.number || 'Sem nome'}
                                                </ContactName>
                                                <TicketDate>
                                                    {formatTicketDate(ticket.updatedAt)}
                                                </TicketDate>
                                            </TicketHeader>

                                            <LastMessage>
                                                <StatusBadge status={ticket.status}>
                                                    {getStatusLabel(ticket.status)}
                                                </StatusBadge>
                                                <span>
                                                    {truncateMessage(ticket.lastMessage)}
                                                </span>
                                            </LastMessage>
                                        </TicketInfo>

                                        <ViewButton
                                            size="small"
                                            onClick={(e) => handleSpyTicket(e, ticket)}
                                            title="Espiar mensagens"
                                        >
                                            <VisibilityIcon />
                                        </ViewButton>
                                        <ViewButton size="small">
                                            <ArrowForwardIcon />
                                        </ViewButton>
                                    </TicketListItemStyled>
                                    {index < tickets.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                        </TicketListContainer>

                        {hasMore && (
                            <LoadMoreButton
                                variant="outlined"
                                onClick={handleLoadMore}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Carregando...
                                    </>
                                ) : (
                                    'Carregar mais'
                                )}
                            </LoadMoreButton>
                        )}
                    </>
                )}
            </StyledDialogContent>

            <MessagesReportModal
                open={messagesModalOpen}
                onClose={handleCloseMessagesModal}
                ticket={selectedTicket}
            />
        </StyledDialog >
    );
}

TicketListModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    user: PropTypes.object,
};
